import React from 'react';

import { InsertDriveFile as FileIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { Typography, Box, Paper, Link } from '@mui/material';

import MessageWrapper from 'pages/Analysis/components/MessageWrapper';
import type { TicketHistoryMessage, TicketMessageContent } from 'services/types/ticket';
import textToReactNodes from 'utils/textToReactNodes';

interface Props {
  message: TicketHistoryMessage;
}

const TicketMessageComponent: React.FC<Props> = ({ message }) => {
  const getCaption = () => {
    if (message.name) {
      return message.name;
    }
    switch (message.role) {
      case 'user':
        return 'Usuario';
      case 'assistant':
        return 'Asistente';
      case 'tool':
        return 'Herramienta';
      default:
        return message.role;
    }
  };

  const renderContent = () => {
    if (typeof message.content === 'string') {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {textToReactNodes(message.content)}
        </Typography>
      );
    }

    if (Array.isArray(message.content)) {
      return (
        <Box>
          {message.content.map((content: TicketMessageContent, index: number) => {
            switch (content.type) {
              case 'text':
                return content.text ? (
                  <Typography
                    key={`${index + 1}-${content.text}`}
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', mb: 1 }}
                  >
                    {textToReactNodes(content.text)}
                  </Typography>
                ) : null;

              case 'image_url':
                return content.image_url ? (
                  <Box key={`${index + 1}-${content.image_url.url}`} sx={{ mb: 1 }}>
                    <img
                      src={content.image_url.url}
                      alt="Imagen adjunta"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : null;

              case 'document':
                return content.document ? (
                  <Paper
                    key={`${index + 1}-${content.document.url}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <FileIcon color="primary" />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Documento
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Formato: {content.document.format.toUpperCase()}
                        </Typography>
                      </Box>
                      <Link href={content.document.url} target="_blank" rel="noopener noreferrer">
                        Ver documento
                      </Link>
                    </Box>
                  </Paper>
                ) : null;

              case 'video':
                return content.video ? (
                  <Paper
                    key={`${index + 1}-${content.video.url}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <PlayIcon color="primary" />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Video
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {content.video.name || `Formato: ${content.video.format.toUpperCase()}`}
                        </Typography>
                      </Box>
                      <Link href={content.video.url} target="_blank" rel="noopener noreferrer">
                        Ver video
                      </Link>
                    </Box>
                  </Paper>
                ) : null;

              default:
                return null;
            }
          })}
        </Box>
      );
    }

    return null;
  };

  return (
    <MessageWrapper caption={getCaption()} align={message.role === 'user' ? 'right' : 'left'}>
      {renderContent()}
    </MessageWrapper>
  );
};

export default TicketMessageComponent;
